/**
 * Head de-duplication for tags react-helmet-async does not own.
 *
 * react-helmet-async only replaces / removes tags that it created itself
 * (marked `data-rh="true"`). Anything already in the document — the server
 * injected SEO head from `apps/seo`, or a static tag in `index.html` — is left
 * alone, so a Helmet `<meta name="description">` ends up NEXT TO it and the
 * page has two descriptions.
 *
 * `removeUnownedHeadDuplicates()` runs from Helmet's `onChangeClientState`
 * (after every DOM commit) and drops every non-Helmet tag whose key is also
 * emitted by Helmet. Helmet's copy is the localized, client-side truth (it
 * already accounts for the per-route / per-page override), so removing the
 * stale copy is safe. Tags Helmet does not emit are never touched.
 */

const HELMET_ATTR = "data-rh";

/** Identity of a `<meta>` for de-dupe purposes, or null when it has none. */
function metaKey(el: Element): string | null {
  const name = el.getAttribute("name");
  if (name) return `name:${name.toLowerCase()}`;
  const property = el.getAttribute("property");
  if (property) return `property:${property.toLowerCase()}`;
  return null;
}

export function removeUnownedHeadDuplicates(
  doc: Document | undefined = typeof document === "undefined"
    ? undefined
    : document,
): number {
  if (!doc?.head) return 0;
  let removed = 0;

  const owned = new Set<string>();
  const unowned: Array<{ el: Element; key: string }> = [];
  doc.head.querySelectorAll("meta").forEach((el) => {
    const key = metaKey(el);
    if (!key) return;
    if (el.hasAttribute(HELMET_ATTR)) owned.add(key);
    else unowned.push({ el, key });
  });
  for (const { el, key } of unowned) {
    if (owned.has(key)) {
      el.remove();
      removed += 1;
    }
  }

  // Several <title> elements (index.html + server injection). `document.title`
  // only ever reads/writes the first one, so the rest are dead weight that
  // crawlers may pick up instead of the real title.
  const titles = doc.head.querySelectorAll("title");
  titles.forEach((el, idx) => {
    if (idx > 0) {
      el.remove();
      removed += 1;
    }
  });

  return removed;
}
